import React, { memo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { ReactFlowProvider } from "reactflow";
import styled from "styled-components";
import { useQuery, useMutation } from "@apollo/client";
import { Box, Loading, Notification } from "orcs-design-system";

import {
  WorkflowBuilderProvider,
  useWorkflowBuilder,
} from "src/contexts/workflowBuilderContext";
import {
  GetWorkflowComponentTypes,
  UpdateWorkflow as UpdateWorkflowMutation,
  PublishWorkflow as PublishWorkflowMutation,
  AddWorkflowStep,
  UpdateWorkflowStep,
  DeleteWorkflowStep,
} from "src/queries/workflows.graphql";

import ComponentPalette from "./ComponentPalette";
import WorkflowCanvas from "./WorkflowCanvas";
import PropertiesPanel from "./PropertiesPanel";

const BuilderContainer = styled.div`
  display: flex;
  height: calc(100vh - 120px);
  width: 100%;
  overflow: hidden;
`;

// Inner component that uses the context
function WorkflowBuilderInner({ workflow, onWorkflowUpdate, onRun }) {
  const {
    setComponentTypes,
    setWorkflow,
    getStepsFromNodes,
    nodes,
    isDirty,
    setIsDirty,
    setError,
    error,
  } = useWorkflowBuilder();

  // Fetch component types
  const { data: componentTypesData, loading: loadingTypes } = useQuery(
    GetWorkflowComponentTypes,
    {
      fetchPolicy: "cache-first",
    }
  );

  // Mutations
  const [, { loading: updating }] = useMutation(UpdateWorkflowMutation);
  const [publishWorkflow, { loading: publishing }] = useMutation(
    PublishWorkflowMutation
  );
  const [addStep] = useMutation(AddWorkflowStep);
  const [updateStep] = useMutation(UpdateWorkflowStep);
  const [deleteStep] = useMutation(DeleteWorkflowStep);

  // Initialize component types
  useEffect(() => {
    if (componentTypesData?.getWorkflowComponentTypes) {
      setComponentTypes(componentTypesData.getWorkflowComponentTypes);
    }
  }, [componentTypesData, setComponentTypes]);

  // Sync workflow prop into context when it changes. Skip when the user has
  // unsaved changes so a background refetch (e.g. cache-and-network) does not
  // overwrite their edits; after save, isDirty is false so the next refetch
  // will correctly update from server.
  useEffect(() => {
    if (workflow && !isDirty) {
      setWorkflow(workflow);
    }
  }, [workflow, isDirty, setWorkflow]);

  // Save workflow. Returns true on success, false on failure (so callers can abort follow-up actions).
  const handleSave = useCallback(async () => {
    if (!workflow?.id) {
      return false;
    }

    try {
      setError(null);
      const steps = getStepsFromNodes();

      // Get existing step IDs
      const existingStepIds = (workflow.steps || []).map((s) => s.id);
      const currentStepIds = steps.filter((s) => s.id).map((s) => s.id);

      // Find steps to delete
      const stepsToDelete = existingStepIds.filter(
        (id) => !currentStepIds.includes(id)
      );

      // Find new steps (no id)
      const newSteps = steps.filter((s) => !s.id);

      // Find existing steps to update
      const existingSteps = steps.filter((s) => s.id);

      // Execute deletions
      await Promise.all(
        stepsToDelete.map((id) => deleteStep({ variables: { id } }))
      );

      // Execute additions
      await Promise.all(
        newSteps.map((step) =>
          addStep({
            variables: {
              input: {
                workflowId: workflow.id,
                stepKey: step.stepKey,
                name: step.name,
                componentType: step.componentType,
                config: step.config || {},
                dependencies: step.dependsOn || [],
                positionX: step.positionX,
                positionY: step.positionY,
              },
            },
          })
        )
      );

      // Execute updates
      await Promise.all(
        existingSteps.map((step) =>
          updateStep({
            variables: {
              id: step.id,
              input: {
                name: step.name,
                config: step.config,
                dependencies: step.dependsOn || [],
                positionX: step.positionX,
                positionY: step.positionY,
              },
            },
          })
        )
      );

      setIsDirty(false);
      onWorkflowUpdate?.();
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save workflow:", err);
      setError(err.message || "Failed to save workflow");
      return false;
    }
  }, [
    workflow,
    getStepsFromNodes,
    addStep,
    updateStep,
    deleteStep,
    setIsDirty,
    setError,
    onWorkflowUpdate,
  ]);

  // Publish workflow
  const handlePublish = useCallback(async () => {
    if (!workflow?.id) {
      return;
    }

    try {
      setError(null);

      // Validate workflow has at least one step
      if (nodes.length === 0) {
        setError("Workflow must have at least one step before publishing");
        return;
      }

      // Save any pending changes first; abort publish if save fails
      if (isDirty) {
        const saved = await handleSave();
        if (!saved) {
          return;
        }
      }

      await publishWorkflow({
        variables: { id: workflow.id },
      });

      onWorkflowUpdate?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to publish workflow:", err);
      setError(err.message || "Failed to publish workflow");
    }
  }, [
    workflow,
    nodes,
    isDirty,
    handleSave,
    publishWorkflow,
    setError,
    onWorkflowUpdate,
  ]);

  if (loadingTypes) {
    return (
      <BuilderContainer>
        <Box p="xl" width="100%" textAlign="center">
          <Loading large />
        </Box>
      </BuilderContainer>
    );
  }

  return (
    <>
      {error && (
        <Notification
          variant="danger"
          closeable
          onClose={() => setError(null)}
          mb="r"
        >
          {error}
        </Notification>
      )}

      <BuilderContainer>
        <ComponentPalette />

        <WorkflowCanvas
          workflow={workflow}
          onSave={handleSave}
          onPublish={handlePublish}
          onRun={onRun}
          isSaving={updating || publishing}
        />

        <PropertiesPanel />
      </BuilderContainer>
    </>
  );
}

WorkflowBuilderInner.propTypes = {
  workflow: PropTypes.object,
  onWorkflowUpdate: PropTypes.func,
  onRun: PropTypes.func,
};

// Main component with providers
function WorkflowBuilder({ workflow, onWorkflowUpdate, onRun }) {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderProvider initialWorkflow={workflow}>
        <WorkflowBuilderInner
          workflow={workflow}
          onWorkflowUpdate={onWorkflowUpdate}
          onRun={onRun}
        />
      </WorkflowBuilderProvider>
    </ReactFlowProvider>
  );
}

WorkflowBuilder.propTypes = {
  workflow: PropTypes.object,
  onWorkflowUpdate: PropTypes.func,
  onRun: PropTypes.func,
};

export default memo(WorkflowBuilder);
